import { ExchangeCode, TokenAvailability } from "@/lib/market-data/market-data.types";
import { isChartableAvailability } from "./candle-symbols";
import {
  CANDLE_INTERVAL_MS,
  CandleFetchParams,
  CandleInterval,
  CandlePoint,
  getExchangeCandlePageSize,
} from "./candle.types";

export type HistoricalCandleResolution =
  | {
      status: "candles";
      exchange: ExchangeCode;
      candles: CandlePoint[];
    }
  | { status: "unavailable" }
  | { status: "rate_limited" };

export interface ResolveHistoricalCandlesParams {
  symbol: string;
  candidates: ExchangeCode[];
  interval: CandleInterval;
  startTime: number;
  endTime: number;
  userId?: string;
  pageLimit?: number;
  /** When the alert window is empty, load the latest candles instead. */
  allowRecentFallback?: boolean;
  checkAvailable: (
    exchange: ExchangeCode,
    symbol: string
  ) => Promise<TokenAvailability>;
  fetchCandles: (
    exchange: ExchangeCode,
    params: CandleFetchParams
  ) => Promise<CandlePoint[]>;
}

function isRateLimitError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : "";
  const lower = message.toLowerCase();
  return (
    lower.includes("429") ||
    lower.includes("rate limit") ||
    lower.includes("too many")
  );
}

/**
 * Try candle exchanges in order. A Binance failure (including a raw HTTP
 * error) moves on to the next exchange. AsterDex reuses Binance klines, so
 * it is skipped after Binance has already failed.
 */
export async function resolveHistoricalCandles(
  params: ResolveHistoricalCandlesParams
): Promise<HistoricalCandleResolution> {
  let binanceFailed = false;

  for (const candidate of params.candidates) {
    if (candidate === "asterdex" && binanceFailed) continue;

    try {
      const availability = await params.checkAvailable(candidate, params.symbol);
      if (!isChartableAvailability(availability)) continue;

      const limit = getExchangeCandlePageSize(candidate, params.pageLimit);
      let candles = await params.fetchCandles(candidate, {
        symbol: params.symbol,
        interval: params.interval,
        startTime: params.startTime,
        endTime: params.endTime,
        limit,
        userId: params.userId,
      });

      if (candles.length === 0 && params.allowRecentFallback) {
        const endTime = Date.now();
        const startTime = endTime - CANDLE_INTERVAL_MS[params.interval] * limit;
        candles = await params.fetchCandles(candidate, {
          symbol: params.symbol,
          interval: params.interval,
          startTime,
          endTime,
          limit,
          userId: params.userId,
        });
      }

      if (candles.length === 0) {
        if (candidate === "binance") binanceFailed = true;
        continue;
      }

      return { status: "candles", exchange: candidate, candles };
    } catch (error) {
      if (candidate === "binance") binanceFailed = true;
      if (isRateLimitError(error)) {
        return { status: "rate_limited" };
      }
    }
  }

  return { status: "unavailable" };
}
